import { Injectable, NgZone } from '@angular/core';
import { User } from '../../models/user';
import { Router } from '@angular/router';
import { SettlementsService } from 'src/app/services/settlements.service';
import { BehaviorSubject, from, map, Observable, switchMap, catchError, of } from 'rxjs';
import { Role } from 'src/app/models/role.model';
import { UserType } from 'src/app/models/user-type.model'; // Assuming you have a UserType model defined


@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private userType$ = new BehaviorSubject<UserType>(this.getUserTypeFromStorage());
  private userPermissions$ = new BehaviorSubject<Set<string>>(new Set());

  // Add helper methods for storage
  private getUserTypeFromStorage(): UserType {
    return (sessionStorage.getItem('userType') as UserType) || 'tenant';
  }

  private setUserTypeInStorage(type: UserType): void {
    sessionStorage.setItem('userType', type);
    this.userType$.next(type);
  }

  userData: any; // Save logged in user data

  constructor(
    public router: Router,
    public ngZone: NgZone, // NgZone service to remove outside scope warning
    private store: SettlementsService
  ) {}

  loadUserPermissions(userId: number) {
    this.userPermissions$.next(new Set());
    // Get user type from storage
    const userType = this.getUserTypeFromStorage();
    
    // Choose appropriate service call based on user type
    const userObservable = userType === 'tenant' 
      ? this.store.getTenant(userId)
      : this.store.getUser(userId);

    return userObservable.pipe(
      switchMap((user: User) => {
        if (!user || !user.role?.id) return of({ permissions: new Set<string>(), userType });

        return from(this.store.getRoles({ id: user.role!.id }))
        .pipe(
          map((roles: Role[] | null) => {
            let permissions = new Set<string>();
            if (roles) {
              roles
                .filter(role => role.id === user.role!.id)
                .forEach(role => {
                  const permissionsArray = JSON.parse(role.permissions!.replace(/'/g, '"'));
                  (permissionsArray || []).forEach((perm: string) => permissions.add(perm))
                });
            }
            return { permissions, userType };
          })
        );
      })
    )
    .subscribe({
      next: ({ permissions, userType }) => {
        this.userPermissions$.next(permissions);
        this.setUserTypeInStorage(userType);
      },
      error: (error) => {
        this.userPermissions$.next(new Set());
        console.error('Error loading user permissions:', error);
        if (error.status === 401) {
          this.handleSessionExpired();
        }
      }
    });   
  }

  public handleSessionExpired(): void {
    this.userPermissions$.next(new Set());
    console.log('Session expired. Redirecting to login...');

    const userType = this.getUserTypeFromStorage();
    
    sessionStorage.removeItem('userType');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('profile');
    sessionStorage.removeItem('avatar');
    sessionStorage.removeItem('permissions');
    
    this.ngZone.run(() => {
      // Route based on user type
      if (!userType) {
        console.error('User type is not defined. Redirecting to sign-in.');
        this.router.navigate(['/sign-in']);
        return;
      }
      const route = userType === 'admin' ? '/ingresar' : '/sign-in';
      this.router.navigate([route]);
    });
  }

  hasPermission(permission: string): boolean {
    return this.userPermissions$.value.has(permission);
  }

  canEditSettlements(): boolean {
    return this.hasPermission('can_edit_settlements');
  }

  getUserPermissions(): Observable<Set<string>> {
    return this.userPermissions$.asObservable();
  }

  // Add helper methods to check user type
  isAdmin(): boolean {
    return this.getUserTypeFromStorage() === 'admin';
  }

  isTenant(): boolean {
    return this.getUserTypeFromStorage() === 'tenant';
  }

  getUserType(): Observable<UserType> {
    return this.userType$.asObservable();
  }

}
